import { MediaLibCategoryEnum } from "@editor/modules/media-lib/types";
import { produce } from "immer";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type { IMediaFile } from "@/shared/types";

interface IState {
  panelId: string | null;

  selectedFolderId: string | null;
  files: IMediaFile[];
  category: MediaLibCategoryEnum;
  searchQuery: string;
  willLoadFiles: boolean;
}

interface IActions {
  setPanelId: (panelId: string | null) => void;
  setSelectedFolderId: (id: string | null) => void;
  setFiles: (data: { files: IMediaFile[]; extend?: boolean }) => void;
  updateFile: (id: string, data: Partial<IMediaFile>) => void;
  removeFile: (id: string) => void;
  setCategory: (value: MediaLibCategoryEnum) => void;
  setSearchQuery: (value: string) => void;
  setWillLoadFiles: (value: boolean) => void;
  archiveFilesInFolders: (folderIds: string[]) => void;
  bulkUpdateFiles: (ids: string[], data: Partial<IMediaFile>) => void;
  bulkDeleteFiles: (ids: string[]) => void;
  reset: () => void;
}

const initState: IState = {
  panelId: null,

  selectedFolderId: null,
  files: [],
  searchQuery: "",
  category: MediaLibCategoryEnum.AllFiles,
  willLoadFiles: false,
};

export const useMediaLibPanelStore = create<IState & IActions>((set) => ({
  ...initState,
  setPanelId: (panelId) => set({ panelId }),
  setFiles: ({ files, extend }) => {
    set((s) => {
      if (!extend) return { files };
      return { files: [...s.files, ...files] };
    });
  },
  updateFile: (id, data) => {
    set((s) => {
      const idx = s.files.findIndex((el) => el._id === id);
      if (idx >= 0) {
        let tmp = [...s.files];
        tmp[idx] = {
          ...tmp[idx],
          ...data,
        };
        if ("folderId" in data) {
          tmp = tmp.filter((file) => file._id !== id);
        }
        return { files: tmp };
      }

      return {};
    });
  },
  removeFile: (id) => {
    set((s) => {
      const idx = s.files.findIndex((el) => el._id === id);
      if (idx >= 0) {
        const tmp = [...s.files];
        tmp.splice(idx, 1);
        return { files: tmp };
      }
      return {};
    });
  },
  bulkUpdateFiles: (ids, data) => {
    set((s) => {
      let tmp = [...s.files];
      ids.forEach((id) => {
        const idx = tmp.findIndex((el) => el._id === id);
        if (idx >= 0) {
          tmp[idx] = {
            ...tmp[idx],
            ...data,
            _id: tmp[idx]._id,
          };
        }
      });

      if ("folderId" in data) {
        tmp = tmp.filter((file) => !ids.includes(file._id));
      }

      return { files: tmp };
    });
  },
  bulkDeleteFiles: (ids) => {
    set((s) => {
      const files = s.files.filter((file) => !ids.includes(file._id));
      return { files };
    });
  },
  archiveFilesInFolders: (fodlerIds) => {
    set((s) => {
      const files = produce(s.files, (draft) => {
        for (const file of draft) {
          if (file.folderId && fodlerIds.includes(file.folderId)) {
            file.archived = true;
          }
        }
      });
      return { files };
    });
  },
  setSelectedFolderId: (selectedFolderId) => set({ selectedFolderId }),
  setCategory: (category) => set({ category }),
  setSearchQuery: (searchQuery) => set({ searchQuery }),
  setWillLoadFiles: (willLoadFiles) => set({ willLoadFiles }),
  reset: () => set(initState),
}));

export const useMediaLibPanelStoreShallow = <U>(
  selector: (store: IState & IActions) => U,
) => {
  return useMediaLibPanelStore(useShallow(selector));
};
